import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  createAction,
} from "@reduxjs/toolkit";
import { Middleware, MiddlewareAPI, Dispatch } from "redux";
import {
  Category,
  Content,
  ContentDetail,
  ContentFilters,
  ContentGroup,
  ContentHeaderOptionItem,
  ContentOptionAppointments,
  ContentOptionDetail,
  ContentOptionDownload,
  ContentOptionFavorite,
  ContentOptionFavorited,
  ContentOptionReadingList,
  ContentOptionSummary,
  ContentReference,
  ContentSummary,
  ContentType as ContentTypeMimeType,
} from "senac-components/dist/pages/Mobile/PageInterfaces";
import { AppDispatch, RootState, Store } from "..";
import { ContentService } from "../../services";
import {
  ContentRelatedRequest,
  CustomContentFilter,
  EntityData,
  RemoteContentDetail,
  RemoteContentReference,
} from "../../services/contents/IContentService";
import common, { commonSlice } from "./common";
import userDataSlice, {
  downloadedContents,
  historyContents,
  insertInFavorite,
  postUserEvaluation,
  removeFromFavorite,
} from "./userDataSlice";
import { userSlice } from "./userSlice";
import Sdk from "api.digitalpages.module.sdk.api";
import { findCategory } from "../../utilities/Categories";

export enum DownloadActionTypes {
  START_DOWNLOAD = "START_DOWNLOAD",
  UPDATE_PROGRESS = "UPDATE_PROGRESS",
  DOWNLOAD_SUCCESS = "DOWNLOAD_SUCCESS",
  DOWNLOAD_FAILURE = "DOWNLOAD_FAILURE",
}

interface ContentState {
  categories: Array<Category>;
  authors: Array<Category>;
  startDownload?: string;
  contentSummary?: Array<ContentSummary>;
  loadingSummary?: boolean;
  mostWantedContent?: string[];
  suggestedContent?: string[];

  activeSummary?: RemoteContentDetail;
  activeContentUid?: string;
  activeContentReference?: any;
  activeContentRelated?: string[];
  activeContentAnchorLink?: string;

  activeReview?: any;

  activeFilter?: CustomContentFilter;
  contentFromFilter?: string[];
  totalContentsFromFilter?: number;

  contentOption?: string;

  allContents: Array<RemoteContentDetail>;

  progressDownload?: DownloadActionTypes;
  preferences?: Array<any>;
  loadingRelateds?: boolean;
  summaryItem?: any;
}

export enum ContentType {
  Epub = "application/epub+zip",
  Pdf = "digitalpages/richpack",
  Audiobook = "digitalpages/audiobook",
  Preference = "",
}

const initialState: ContentState = {
  categories: [],
  authors: [],
  allContents: [],
  preferences: [],
};

export const mostWantedContents = createAsyncThunk("contents/mostwanted", () =>
  ContentService.MostWantedContents()
);

export const suggestedContents = createAsyncThunk(
  "contents/suggestedContents",
  () => ContentService.SuggestedContents()
);

export const loadContentDetail = createAction<string>("content/detail/load");

export const contentDetail = createAsyncThunk("contents/detail", (contentUid: string) => ContentService.ContentDetail(contentUid));

export const contentDetailResumed = createAsyncThunk("contents/detail/resumed", (contentUid: string) => ContentService.ContentDetailResumed(contentUid));

export const contentFilter = createAsyncThunk("contents/filter", (filter: CustomContentFilter) => ContentService.Contents(filter));

const contentRelated = createAsyncThunk("contents/related",(info: ContentRelatedRequest) => ContentService.RelatedContents(info));

export const schemasAll = createAsyncThunk("categories/all", () => ContentService.Schemas());

export const contentSummary = createAsyncThunk("contents/summaries",(content: Content) => ContentService.ContentSummary(content));

export const showContentDetail = createAction<string>("content/detail/show");

export const showContentDetailWithMarks = createAction<string>("content/detail/show");

export const loadAndOpenContent = createAction<string>("content/load/open");

export const openContent = createAction<ContentType[]>("content/open");

export const loadMoreFromActiveContentFilter = createAction("content/load/more");

export const updateDownloadProgress = createAction<number>("content/download/progress");

export const activeContentAnchor = createAction<any>("content/active/anchor");

export const contentSlice = createSlice({
  name: "contentSlice",
  initialState,
  reducers: {
    setSummaryItem: (state, action) => {
      state.summaryItem = action.payload
    },
    setContentOption: (state, action) => {
      state.contentOption = action.payload
    },
    resetState: () => initialState,
    setLoadingSummary: (state, action) => {
      state.loadingSummary = action.payload;
    },
    openOfflineContent: (state, action) => {
      state.activeContentUid = action.payload.uid
    },
    updateAllContents: (state, action) => {
      updateData(state.allContents, action.payload);
    },
    updateImagePermissions:(state) =>
    {
      for(var content of state.allContents)
      {
        if (content.image?.indexOf("http://localhost") != -1)
        {
          content.image= Sdk.Api.dynamic.bridgeUrl(`cms/v1.1/managed/content/group/uid/${content.uid}/default/thumb`,null);
        }
      }
    },
    updateDownloadProgress: (state, action) => {
      state.preferences = action.payload;
    },

    setActiveContentUid: (state, action) => {
      state.activeContentUid = action.payload;
    },

    contentFilter: (state, filter: PayloadAction<ContentFilters>) => {
      const data = filter.payload;
      const isEqual = JSON.stringify(data) == JSON.stringify(state.activeFilter ?? {});

      if (isEqual) return;

      let filterUpdated:CustomContentFilter = data;

      if (data.category) {
        var filterCategory = data.category;
        var foundCategory = state.categories
          .map((category) => findCategory(filterCategory.uid, category))
          .filter((p) => p !== undefined)?.[0];

        if (!foundCategory) {
          foundCategory = {
            uid: filterCategory.uid,
            label: "Categoria não encontrada",
          };
        }

        filterUpdated.category = foundCategory;
      }else
      {
        filterUpdated.category = undefined;
      }

      filterUpdated.processing = true;

      state.activeFilter = filterUpdated
      state.contentFromFilter = undefined;
      state.totalContentsFromFilter = undefined;
    },
    goToContentInfo: (
      state,
      option: PayloadAction<ContentHeaderOptionItem | undefined>
    ) => {
      const optionSelected = option.payload;
      let options = undefined;

      if (optionSelected === ContentOptionDetail) {
        options = "detail";
      } else if (optionSelected === ContentOptionAppointments) {
        options = "appointments";
      } else if (
        optionSelected === ContentOptionFavorite ||
        optionSelected === ContentOptionFavorited ||
        optionSelected === ContentOptionDownload
      ) {
        options = 'detail';
        ///evento tratado por middleware
      } else if (optionSelected === ContentOptionReadingList) {
        options = "readinglist";
      } else if (optionSelected === ContentOptionSummary) {
        options = "summary";
      }

      state.contentOption = options;
    },
    
    updateContentAnchor: (state, data) => {
      state.activeContentAnchorLink = data.payload
    },
    
    activeReference: (state, data: PayloadAction<any>) => {
      state.activeContentReference = data.payload;
    },
    contentSummary: (state, data: PayloadAction<RemoteContentDetail>) => {
      state.contentSummary = undefined;
    },
    startDownload: (state, data: PayloadAction<string>) => {
      state.startDownload = data.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(contentSummary.fulfilled, (state, action) => {
      state.contentSummary = action.payload;
      state.loadingSummary = false;
    });

    builder.addCase(contentSummary.pending, (state, action) => {
      state.loadingSummary = true;
    });

    builder.addCase(contentSummary.rejected, (state, action) => {
      state.contentSummary = [];
      state.loadingSummary = false;
    });

    builder.addCase(mostWantedContents.fulfilled, (state, action) => {
      state.mostWantedContent = action.payload.map((p) => p.uid);
      updateData(state.allContents, action.payload);
      updateContentAuthor(state.authors, state.allContents);
    });

    builder.addCase(suggestedContents.fulfilled, (state, action) => {
      state.suggestedContent = action.payload.map((p) => p.uid);
      updateData(state.allContents, action.payload);
      updateContentAuthor(state.authors, state.allContents);
    });

    builder.addCase(contentRelated.pending, (state, action) => {
      state.activeContentRelated = undefined;
      state.loadingRelateds = true;
    });

    builder.addCase(contentRelated.rejected, (state, action) => {
      state.activeContentRelated = undefined;
      state.loadingRelateds = true;
    });
    
    builder.addCase(contentRelated.fulfilled, (state, action) => {
      state.activeContentRelated = action.payload.map((p) => p.uid);
      state.loadingRelateds = false;
      updateData(state.allContents, action.payload);
      updateContentAuthor(state.authors, state.allContents);
    });

    builder.addCase(historyContents.fulfilled, (state, action) => {
      updateData(state.allContents, action.payload);
      updateContentAuthor(state.authors, state.allContents);
    });

    builder.addCase(contentFilter.pending, (state, action) => {
      if (state.activeFilter){
        state.activeFilter.processing = true
      }
    });

    builder.addCase(contentFilter.rejected, (state, action) => {
      if (state.activeFilter){
        state.activeFilter.processing = false
      }
    });

    builder.addCase(contentFilter.fulfilled, (state, action) => {
      const searchResult = action.payload;

      if (!state.contentFromFilter) state.contentFromFilter = [];

      state.contentFromFilter = [
        ...state.contentFromFilter,
        ...searchResult.result.map((p) => p.uid),
      ];

      state.totalContentsFromFilter = searchResult.totalResult;

      if (state.activeFilter){
        state.activeFilter.customQuery = searchResult.nextQuery;
      }

      updateData(state.allContents, action.payload.result);
      updateContentAuthor(state.authors, state.allContents);

      if(state.activeFilter) {
        state.activeFilter.processing = false
      }

    });

    builder.addCase(downloadedContents.fulfilled, (state, action) =>
    {
      var statusUpdated = action.payload;

      for (var download of statusUpdated)
      {
        var inMemory = state.allContents.find(p=> p.uid == download.uid);

        if (!inMemory)
        {
          state.allContents.push(download);
          continue;
        }

        for(var reference of download.references ?? [])
        {
          var currentReference = inMemory.references?.find(p=> p.uid == reference.uid);

          if (!currentReference)
          {
            if (!inMemory.references) inMemory.references = [];
            inMemory.references?.push(reference);
          }else
          {
            currentReference.downloadProgress = reference.downloadProgress;
          }
        }
      }

      state.allContents.forEach(c=>{
        var receiveStatus = action.payload?.find(r=> r.uid == c.uid);
        if (receiveStatus) return;

        c.references?.forEach(r=> r.downloadProgress = undefined);
      });
    });

    builder.addCase(schemasAll.fulfilled, (state, action) => {
      var data = action.payload;
      var categories = data.find(
        (schema) => schema.name === "Categories" || schema.name === "Categorias" || schema.name === "Categoria"
      )?.categories;
      var authors = data.find(
        (schema) => schema.name === "Authors" || schema.name === "Autores"
      )?.categories;

      state.categories = categories ?? [];
      state.authors = authors ?? [];
    });

    builder.addCase(contentDetail.pending, (state, action) => {
      state.activeContentUid = action.meta.arg;
      state.loadingRelateds = true;
    });

    builder.addCase(contentDetail.fulfilled, (state, action) => {
      var contentUpdated = action.payload;
      state.activeReview = action.payload.fullReview
      updateContentAuthor(state.authors, [contentUpdated]);
      updateData(state.allContents, [contentUpdated]);
    });

    builder.addCase(contentDetailResumed.fulfilled, (state, action) => {
      var contentUpdated = action.payload;
      updateContentAuthor(state.authors, [contentUpdated]);
      updateData(state.allContents, [contentUpdated]);
    });
  },
});

export const middleware: Middleware =
  (store: MiddlewareAPI<AppDispatch, RootState>) =>
  (next: Dispatch) =>
  async (action: PayloadAction<any>) => {
    const state = store.getState();
    if (contentSlice.actions.openOfflineContent.match(action)) {
      const { referenceType, uid } = action.payload;
      var activeContent = state.contentState.allContents.find(p => p.uid === uid);
      var fail = true;

      if (activeContent) {
        var reference = activeContent.references?.find(
          (p) => p.mimeType === referenceType
        );

        if (reference) {
          store.dispatch(contentSlice.actions.setActiveContentUid(activeContent.uid));
          store.dispatch(contentSlice.actions.activeReference(reference));
          store.dispatch(commonSlice.actions.goToContent({contentReferenceUid: reference.uid, contentUid:activeContent.uid}));
          fail = false;
        }
      }

      if (fail) {
        store.dispatch(
          commonSlice.actions.showAlert({
            title: "Conteúdo",
            description: "Não foi possível abrir o formato escolhido.",
            show: true,
          })
        );
      }
    };

    if (loadContentDetail.match(action)) {
      store.dispatch(contentSlice.actions.setLoadingSummary(true))
      store.dispatch(contentDetail(action.payload)).unwrap()
      .then((result) => {
        if (!state.commonState.runningInMobile && window.innerWidth > 1024) {
          store.dispatch(contentSummary(result));
        }
      })
    };

    if(activeContentAnchor.match(action)) {
      const option = action.payload;
      if(!option) return;

      if(!option && state.contentState.activeContentAnchorLink) {
        store.dispatch(contentSlice.actions.updateContentAnchor(undefined))
      }

      if(option.contentType === 'pdf') {
        store.dispatch(contentSlice.actions.updateContentAnchor(option.contentLink?.navLink ?? ''))
        store.dispatch(openContent([ContentType.Pdf]));
        return;
      };

      const mark = state.userDataState.userData.find(item => item.uid === option?.contentLink?.uid);

      const anchor = mark ? JSON.stringify(mark) : option?.contentLink?.navLink ? option?.contentLink?.navLink : '';
      if (option.contentType === 'epub') {
        store.dispatch(contentSlice.actions.updateContentAnchor(anchor))
        store.dispatch(openContent([ContentType.Epub]))
        return;
      }

      store.dispatch(contentSlice.actions.updateContentAnchor(anchor))
        store.dispatch(openContent([ContentType.Audiobook]))
    }

    if (action.payload === ContentOptionDownload) {
    }

    if (userSlice.actions.activeUser.match(action)) {
      const user = action.payload;

      store.dispatch(schemasAll());
      store.dispatch(contentSlice.actions.updateImagePermissions());

      if (user) {
        store.dispatch(suggestedContents());
      } else {
        store.dispatch(mostWantedContents());
      }
    }

    if (contentDetail.fulfilled.match(action)) {
      store.dispatch(contentRelated({
        content: action.payload,
        categoriesAvailableForUser: state.contentState.categories
      }));
    }

    if (contentSlice.actions.contentFilter.match(action) && state.contentState.activeFilter?.processing != true)
    {
      store.dispatch(contentFilter(action.payload));
    }

    if (loadMoreFromActiveContentFilter.match(action) && state.contentState.activeFilter) {
      store.dispatch(contentFilter(state.contentState.activeFilter));
    }

    if (loadAndOpenContent.match(action)) {
      if (state.commonState.withInternet == false) {
        var contentReferenceDownloaded = state.contentState.allContents.find((p) => p?.uid == action.payload);

        if (!contentReferenceDownloaded ){//|| !contentDownloaded) {
        } else {
          // var contentUpdated: RemoteContentDetail = {
          //   ...contentDownloaded,
          //   references: [contentReferenceDownloaded],
          // };

          // store.dispatch(contentSlice.actions.contentDetail(contentDownloaded.uid));
          store.dispatch(
            openContent([
              ContentType.Preference,
              ContentType.Epub,
              ContentType.Pdf,
              ContentType.Audiobook,
            ])
          );
        }
      } else {
        store
          .dispatch(contentDetail(action.payload))
          .then(() =>
            store.dispatch(
              openContent([
                ContentType.Preference,
                ContentType.Epub,
                ContentType.Pdf,
                ContentType.Audiobook,
              ])
            )
          );
      }
    }

    if (
      contentSlice.actions.goToContentInfo.match(action) &&
      action.payload === ContentOptionSummary &&
      state.contentState.activeContentUid
    ) {
      var activeContentReference = state.contentState.allContents.find(p=> p.uid == state.contentState.activeContentUid);
      if (activeContentReference) store.dispatch(contentSummary(activeContentReference));
    }

    if (
      contentSlice.actions.goToContentInfo.match(action) &&
      action.payload === ContentOptionFavorite &&
      state.contentState.activeContentUid
    ) {
      var content = state.contentState.allContents.find(p=> p.uid == state.contentState.activeContentUid);
      if (content) store.dispatch(insertInFavorite(content));
    }

    if (
      contentSlice.actions.goToContentInfo.match(action) &&
      action.payload === ContentOptionFavorited &&
      state.contentState.activeContentUid
    ) {
      var content = state.contentState.allContents.find(p=> p.uid == state.contentState.activeContentUid);
      if (content) store.dispatch(removeFromFavorite(content));
    }

    if (contentSlice.actions.setSummaryItem.match(action)) {
      if (action.payload) {
        store.dispatch(openContent([action.payload.type === 'pub' ? ContentType.Epub : action.payload.type === 'pdf' ? ContentType.Pdf : ContentType.Audiobook]));
      }
    }
    if (openContent.match(action)) {
      var activeContent = state.contentState.allContents.find(p=> p.uid == state.contentState.activeContentUid);
      var fail = true;

      if (activeContent) {
        var types = action.payload;

        for (var type of types) {
          if (type == ContentType.Preference) {
            type = ContentType.Epub;
          }

          var reference = activeContent.references?.find(
            (p) => p.mimeType == type
          );

          var audioRef : RemoteContentReference = {
            uid: "H4sIAAAAAAAAAwBCAb3-AwAAASIOAAAAADUyZGYzYzkxy_FyDWKGP0k8-n-3tufSMOca0kjF3j6I__rKn8RAcPlpXck7ASU5Fl-6-veNgyne-R8rnJFkXPRpVSo1yAjGJ9jWBRTkINMkCSx2XROSJHQRHLPaQtLlvtrH5dlbNI6KU3ONmbj-w0GYyK8DQeufZ3mNp8Tmuu4Dpetdu9MZI_eiRT9PS8j1Bi0DdRKBGx6WxLqJiZcYlZAD3mWoh6kwitSZsnelZfjrnQVlWqNLan-lGLu-BU0VtKi5T0LsfR_YL1xh8eSe3SQOY539GSBXJrhouYDX-5tzmrC9ireF418SBf4jBmxIK234cy4F2PGthdiYYcD1SARBsCwvlAmTvc_LdnvxrZ8s5XG2Sp1cly8Kmkp-CoR_obV8WbqG6CiThc8PxdzaNFHLfTshz94J7QAAAP__",
            mimeType: "digitalpages/audiobook",
            products:[],
            url: "https://api.digitalpages.com.br/product/composer_player/current/a59e9223e8ca4e5ab371d20c72bb5479/d/-/prd/init?custom_options=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#/",
            format: ContentTypeMimeType.AUDIOBOOK,
            downloadProgress: undefined,
          }

          // reference = audioRef;

          if (reference) {
            store.dispatch(contentSlice.actions.activeReference(reference));
            store.dispatch(commonSlice.actions.goToContent({
              contentReferenceUid: reference.uid, 
              contentUid:activeContent.uid, 
              ...(state.contentState.summaryItem && {
                summary: state.contentState.summaryItem
              })
            }));
            fail = false;
            break;
          }
        }
      }

      if (fail) {
        store.dispatch(
          commonSlice.actions.showAlert({
            title: "Conteúdo",
            description: "Não foi possível abrir o formato escolhido.",
            show: true,
          })
        );
      }
    }

    return next(action);
  };

export const updateData = (currentData: RemoteContentDetail[], newDatas: RemoteContentDetail[]) =>
{
  for (var newData of newDatas) {
    var current = currentData.find((p) => p.uid == newData.uid);

    if (current == null)
    {
      currentData.push(newData);
      continue;
    }

    var currentReferences = [ ...current.references ?? [] ];
    var currentGeneric = current as Record<string, any>;
    var neweneric = newData as Record<string, any>;

    for (var key in neweneric)
    {
      currentGeneric[key] = neweneric[key]
    }

    for(var reference of currentReferences ?? [])
    {
      var newReference = current.references?.find(p=> p.uid == reference.uid);

      if (newReference == null) continue;
      if (newReference.downloadProgress == undefined)
      {
        newReference.downloadProgress = reference.downloadProgress;
      }
    }
  }
};

const updateContentAuthor = (
  authors: Category[],
  contents: RemoteContentDetail[]
) => {
  for (const content of contents) {
    const names = authors
      .filter((p) => content.entities?.find((e) => e.entityUid === p.uid))
      .map((p) => p.label)
      .join(" | ");

    content.author = names ?? "Não definido";
  }
};

// const localContentInfo = (state: RootState, contentUid: string) => {
//   var result: Content | undefined =
//     state.contentState.mostWantedContent?.books?.find(
//       (p) => p.uid == contentUid
//     );

//   if (!result)
//     result = state.contentState.suggestedContent?.books?.find(
//       (p) => p.uid == contentUid
//     );
//   if (!result)
//     result = state.contentState.contentFromFilter?.find(
//       (p) => p.uid == contentUid
//     );
//   if (!result) result = { uid: contentUid, title: "Carregando" };

//   return result;
// };

const referenceToDownload = (content: RemoteContentDetail) => {
  if (content.references && content.references?.length > 0) {
    var reference = content.references.find(
      (p) => p.mimeType.indexOf("epub") != -1
    );
    if (!reference) reference = content.references[0];

    return reference;
  }

  return null;
};

export default contentSlice.reducer;
